// 面板頁上的行事曆卡片（docs/panel/09-voice-calendar.md §5.3、§8；05-ui-ux V-23；VC1a）。
//   - PanelCalendarEntryCard：［打開行事曆］→ /devices/{id}/calendar。主人一定看得到；其他家人先讀一次行事曆，
//     403 CALENDAR_FORBIDDEN / 404 就不顯示（不是行事曆家人看不到入口，§5.3）。
//   - PanelCalendarPeopleCard（只限主人）：長輩名字 residents、長輩怎麼叫家人 family_call_names（D9）、
//     「能管行事曆」勾選 calendar_managers（null = 自動：主人 + 設了叫法的家人；改任何一列就存成明確清單，［恢復預設］回自動）。
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import { currentUserId, getPanelSettings, putPanelSettings } from './panels';
import {
  CALL_NAMES_MAX, RESIDENTS_MAX, calErr, calErrKey, calSettingsBody, effectiveManagers, getPanelCalendar, namesError, parseCalSettings, splitNames, toggleManager,
} from './panelCalendar';

export function PanelCalendarEntryCard({ panelId, isOwner }: { panelId: string; isOwner: boolean }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(isOwner);
  useEffect(() => {
    if (isOwner) return;
    let alive = true;
    getPanelCalendar(panelId)
      .then(() => alive && setShow(true))
      .catch((e) => {
        const k = calErr(e).kind;
        if (alive) setShow(k !== 'forbidden' && k !== 'notFound');
      });
    return () => {
      alive = false;
    };
  }, [panelId, isOwner]);
  if (!show) return null;
  return (
    <div className="card" data-testid="panel-calendar-entry">
      <h2>{t('panelCal.title')}</h2>
      <p className="text-secondary">{t('panelCal.entryHint')}</p>
      <Link className="btn-primary" to={`/devices/${encodeURIComponent(panelId)}/calendar`} data-testid="panel-calendar-open">
        {t('panelCal.open')} ›
      </Link>
    </div>
  );
}

interface FamilyRow {
  user_id: string;
  name: string;
  call_names: string[];
}

function parseFamilyRows(raw: unknown): FamilyRow[] {
  const list = (raw as { family?: unknown } | null)?.family;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v) => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    if (typeof o.user_id !== 'string' || !o.user_id) return [];
    const cn = Array.isArray(o.call_names) ? o.call_names.filter((x): x is string => typeof x === 'string') : [];
    return [{ user_id: o.user_id, name: typeof o.name === 'string' && o.name.trim() ? o.name.trim() : o.user_id, call_names: cn }];
  });
}

export function PanelCalendarPeopleCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const me = currentUserId();
  const [family, setFamily] = useState<FamilyRow[] | null>(null);
  const [residentsText, setResidentsText] = useState('');
  const [callText, setCallText] = useState<Record<string, string>>({});
  const [managers, setManagers] = useState<string[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([getPanelSettings(panelId), apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/family`)])
      .then(([s, f]) => {
        if (!alive) return;
        const cs = parseCalSettings(s);
        const rows = parseFamilyRows(f.data);
        setFamily(rows);
        setResidentsText(cs.residents.join('、'));
        const ct: Record<string, string> = {};
        for (const r of rows) ct[r.user_id] = (cs.callNames[r.user_id] ?? r.call_names).join('、');
        setCallText(ct);
        setManagers(cs.managers);
      })
      .catch((e) => alive && setLoadErr(calErrKey(e)));
    return () => {
      alive = false;
    };
  }, [panelId]);

  const owner = me ?? '';
  const ids = (family ?? []).map((r) => r.user_id);
  const callNames: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(callText)) callNames[k] = splitNames(v);
  const effective = effectiveManagers(owner, ids, callNames, managers);

  const save = async () => {
    const residents = splitNames(residentsText);
    const rErr = namesError(residents, RESIDENTS_MAX);
    if (rErr) {
      setErr(rErr === 'tooMany' ? t('panelCal.settings.tooMany', { n: RESIDENTS_MAX }) : t('panelCal.settings.tooLong'));
      return;
    }
    for (const v of Object.values(callNames)) {
      const e = namesError(v, CALL_NAMES_MAX);
      if (e) {
        setErr(e === 'tooMany' ? t('panelCal.settings.tooMany', { n: CALL_NAMES_MAX }) : t('panelCal.settings.tooLong'));
        return;
      }
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const saved = parseCalSettings(await putPanelSettings(panelId, calSettingsBody(residents, callNames, managers)));
      setResidentsText(saved.residents.join('、'));
      setManagers(saved.managers);
      setMsg(t('panelCal.settings.saved'));
    } catch (e) {
      setErr(t(calErrKey(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" data-testid="panel-calendar-people">
      <h2>{t('panelCal.settings.title')}</h2>
      <p className="text-secondary">{t('panelCal.settings.hint')}</p>
      {loadErr && <p className="error-text" role="alert">{t(loadErr)}</p>}
      {family && (
        <>
          <label className="pc-field">
            <span>{t('panelCal.settings.residents')}</span>
            <input value={residentsText} placeholder={t('panelCal.settings.residentsPh')} data-testid="cal-residents"
              onChange={(e) => { setResidentsText(e.target.value); setMsg(null); }} />
            <small className="text-secondary">{t('panelCal.settings.residentsHint')}</small>
          </label>

          <h3 className="pc-subhead">{t('panelCal.settings.family')}</h3>
          <p className="text-secondary" data-testid="cal-managers-mode">{t(managers === null ? 'panelCal.settings.auto' : 'panelCal.settings.explicit')}</p>
          {family.length === 0 && <p className="text-secondary">{t('panelCal.settings.empty')}</p>}
          <ul className="pc-family">
            {family.map((r) => {
              const isOwnerRow = r.user_id === owner;
              const on = effective.includes(r.user_id);
              const names = callNames[r.user_id] ?? [];
              return (
                <li key={r.user_id} className="pc-famrow" data-testid={`cal-fam-${r.user_id}`}>
                  <strong>{r.name}{isOwnerRow ? t('panelCal.settings.you') : ''}</strong>
                  <label className="pc-field">
                    <span>{t('panelCal.settings.callNames')}</span>
                    <input value={callText[r.user_id] ?? ''} placeholder={t('panelCal.settings.callNamesPh')} data-testid={`cal-callnames-${r.user_id}`}
                      onChange={(e) => { setCallText({ ...callText, [r.user_id]: e.target.value }); setMsg(null); }} />
                    {managers === null && !isOwnerRow && names.length > 0 && (
                      <small className="text-secondary" data-testid={`cal-autohint-${r.user_id}`}>{t('panelCal.settings.autoHint')}</small>
                    )}
                  </label>
                  <label className="pc-check">
                    <input type="checkbox" checked={isOwnerRow || on} disabled={isOwnerRow} data-testid={`cal-manager-${r.user_id}`}
                      onChange={(e) => { setManagers(toggleManager(owner, ids, callNames, managers, r.user_id, e.target.checked)); setMsg(null); }} />
                    {t('panelCal.settings.manager')}
                    {isOwnerRow && <small className="text-secondary"> {t('panelCal.settings.ownerAlways')}</small>}
                  </label>
                </li>
              );
            })}
          </ul>
          {err && <p className="error-text" role="alert">{err}</p>}
          {msg && <p className="text-secondary" role="status">{msg}</p>}
          <div className="dv-pairing-actions">
            <button className="btn-primary" disabled={busy} onClick={() => void save()} data-testid="cal-people-save">
              {busy ? t('common.saving') : t('common.save')}
            </button>
            {managers !== null && (
              <button className="btn-ghost" disabled={busy} onClick={() => { setManagers(null); setMsg(null); }} data-testid="cal-managers-restore">
                {t('panelCal.settings.restore')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
